import { create } from 'zustand';
import { ApiMeResponse, teamsSetupApi } from '../services/teamsSetupApi';
import {
  buildFetchErrorMessage,
  DEFAULT_BOARD_TITLE,
  resolveTeamsIdentity,
  type TeamsAuthenticationLike,
} from '../teams/services/teamsIdentity';

export type TeamsContext = Record<string, unknown>;

export type TeamsAuthRuntime = {
  authentication?: {
    getAuthToken: (request?: {
      resources?: string[];
      silent?: boolean;
      successCallback?: (token: string) => void;
      failureCallback?: (reason?: string) => void;
    }) => Promise<string> | void;
    authenticate: (request: {
      url: string;
      width?: number;
      height?: number;
      successCallback?: (result: string) => void;
      failureCallback?: (reason?: string) => void;
    }) => Promise<string> | void;
  };
};

type TeamsSetupStoreState = {
  apiMe: ApiMeResponse | null;
  loginError: string | null;
  isLoggingIn: boolean;
  appAuthToken: string | null;
  defaultBoardTitle: string;
  setLoginError: (value: string | null) => void;
  resetAuthState: () => void;
  loginWithTeams: (input: { teams: TeamsAuthRuntime; context: TeamsContext }) => Promise<void>;
};

const useTeamsSetupStore = create<TeamsSetupStoreState>((set) => ({
  apiMe: null,
  loginError: null,
  isLoggingIn: false,
  appAuthToken: null,
  defaultBoardTitle: DEFAULT_BOARD_TITLE,
  setLoginError: (value) => set({ loginError: value }),
  resetAuthState: () =>
    set({
      apiMe: null,
      loginError: null,
      isLoggingIn: false,
      appAuthToken: null,
      defaultBoardTitle: DEFAULT_BOARD_TITLE,
    }),
  loginWithTeams: async ({ teams, context }) => {
    set({ isLoggingIn: true, loginError: null });

    try {
      await new Promise((resolve) => setTimeout(resolve, 0));
      const identity = await resolveTeamsIdentity({
        authentication: teams.authentication as TeamsAuthenticationLike | undefined,
        context,
      });

      set({
        apiMe: identity.apiMe,
        appAuthToken: identity.appAuthToken,
        defaultBoardTitle: identity.defaultBoardTitle,
      });
    } catch (error) {
      set({ loginError: buildFetchErrorMessage(error, teamsSetupApi.getMeUrl()) });
    } finally {
      set({ isLoggingIn: false });
    }
  },
}));

export default useTeamsSetupStore;
